import {
  action, computed, makeAutoObservable, observable, observe, runInAction, when,
} from 'mobx';
import { SdkStore } from '@gamiphy/loyalty-station-desktop/store/sdk';
import { DiscountSources } from '@gamiphy/bl/cashbackDiscount';
import {
  CashbackSummaryResponse,
  DiscountDto,
  DiscountDtoStatusEnum,
  DiscountsApi,
  DiscountsControllerListSourceEnum,
  WelcomeGiftApi,
} from '@coretava/api/ecommerce-service';
import { delay } from '@gamiphy/utils/general';
import { AppStore } from '../app';
import { UserStore } from '../user';
import { AppCashbackStore } from '../appCashback';

class DiscountsStore {
  @observable initialized = false;

  @observable bestFitDiscount?: CashbackSummaryResponse = undefined;

  @observable welcomeGiftDiscount?: DiscountDto = undefined;

  constructor() {
    makeAutoObservable(this);
  }

  @computed
  get api(): DiscountsApi {
    return new DiscountsApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get welcomeGiftApi(): WelcomeGiftApi {
    return new WelcomeGiftApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get expectedDiscountValue(): number {
    return Math.round((SdkStore.cart?.data?.itemsTotal ?? 0) * ((AppCashbackStore.defaultAppCashback?.percentage ?? 0) / 100));
  }

  @computed
  get isWelcomeGiftDiscount(): boolean {
    return this.bestFitDiscount?.applicableDiscount.discount?.source === DiscountSources.WelcomeGift;
  }

  @computed
  get isWelcomeGiftDiscountValid(): boolean {
    return this.welcomeGiftDiscount?.status === DiscountDtoStatusEnum.Valid;
  }

  @action
  async initialize(): Promise<void> {
    await when(() => AppStore.initialized && !!SdkStore.cart);

    observe(SdkStore.cart!, 'data', async () => {
      await this.getBestFitDiscount();
    });

    observe(this, 'bestFitDiscount', async () => {
      await this.applyCashbackDiscount();
    });

    await this.getDiscounts();

    runInAction(() => {
      this.initialized = true;
    });
  }

  @action
  async getBestFitDiscount(): Promise<void> {
    await when(() => UserStore.isRealUser && !!SdkStore.cart);
    const response = await this.api.discountsControllerGetCashbackSummaryByUser(AppStore.app.id, SdkStore.cart?.data?.itemsTotal ?? 0, 'online');

    runInAction(() => {
      this.bestFitDiscount = response.data;
    });
  }

  @action
  async getWelcomeGiftDiscount(): Promise<void> {
    await when(() => AppStore.initialized && UserStore.isRealUser);

    const response = await this.api.discountsControllerList(
      AppStore.app.id,
      undefined,
      DiscountsControllerListSourceEnum.WelcomeGift,
    );

    runInAction(() => {
      [this.welcomeGiftDiscount] = response.data.discounts;
    });
  }

  @action
  async getDiscounts(): Promise<void> {
    await when(() => AppStore.initialized && UserStore.isRealUser);

    await Promise.all([
      this.getBestFitDiscount(),
      this.getWelcomeGiftDiscount(),
    ]);

    void delay(1000).then(async () => {
      await Promise.all([
        this.getBestFitDiscount(),
        this.getWelcomeGiftDiscount(),
      ]);
    });
  }

  @action
  async generateWelcomeGiftDiscount(): Promise<void> {
    await when(() => AppStore.initialized && UserStore.isRealUser && this.initialized);

    if (!this.welcomeGiftDiscount) {
      const response = await this.welcomeGiftApi.welcomeGiftControllerCreateUserWelcomeGift(AppStore.app.id);

      runInAction(() => {
        this.welcomeGiftDiscount = response.data.UserWelcomeGift;
      });
    }
  }

  @action
  public async applyCashbackDiscount(): Promise<void> {
    await when(() => this.initialized && !!SdkStore.cart);

    if (this.bestFitDiscount?.applicableDiscount.discount) {
      const hasDiscountApplied = !!SdkStore.cart?.data?.discountCodes.some((code) => code === this.bestFitDiscount?.applicableDiscount.discount?.code);

      if (this.bestFitDiscount.applicableDiscount.amount > 0) {
        if (!hasDiscountApplied) {
          await SdkStore.cart?.applyDiscount(this.bestFitDiscount.applicableDiscount.discount.code);
        }
      } else if (hasDiscountApplied) {
        await SdkStore.cart?.removeDiscount(this.bestFitDiscount.applicableDiscount.discount.code);
      }
    }
  }
}

export default new DiscountsStore();
